import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/forms/Checkbox';
import { Input } from '../../../../components/forms/Input';
import { Textarea } from '../../../../components/forms/Textarea';
import { Modal } from '../../../../components/Modal';
import { useResources, useServiceMutations, useServices } from '../hooks';
import {
  isProtectedByBookings,
  SchedulingApiError,
  SchedulingProfile,
  Service,
  ServiceVariant,
} from '../types';
import { ShowInactiveToggle } from '../components/ShowInactiveToggle';
import { formatPrice } from '../../../../utils/formatPrice';
import { formatDuration } from '../utils';

interface FormState {
  id: string | null;
  name: string;
  description: string;
  duration: string;
  buffer: string;
  price: string;
  resources: string[];
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: '',
  description: '',
  duration: '60',
  buffer: '0',
  price: '',
  resources: [],
  isActive: true,
};

const toForm = (service: Service): FormState => ({
  id: service.id,
  name: service.name,
  description: service.description,
  duration: String(service.duration_minutes),
  buffer: String(service.buffer_after_minutes),
  price: service.price ?? '',
  resources: service.resources,
  isActive: service.is_active,
});

const VariantRow: React.FC<{
  variant: ServiceVariant;
  onSave: (data: {
    name: string;
    duration: string;
    buffer: string;
    price: string;
  }) => Promise<void>;
  onRemove: () => void;
}> = ({ variant, onSave, onRemove }) => {
  const { t } = useTranslation();
  const initial = {
    name: variant.name,
    duration: String(variant.duration_minutes),
    buffer: variant.buffer_after_minutes === null ? '' : String(variant.buffer_after_minutes),
    price: variant.price ?? '',
  };
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = (Object.keys(initial) as (keyof typeof initial)[]).some(
    key => values[key] !== initial[key]
  );

  return (
    <div className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 items-end">
      <Input
        label={t('schedulingAdmin.services.variantName')}
        value={values.name}
        onChange={e => setValues({ ...values, name: e.target.value })}
        fullWidth
      />
      <Input
        type="number"
        min={1}
        label={t('schedulingAdmin.services.duration')}
        value={values.duration}
        onChange={e => setValues({ ...values, duration: e.target.value })}
        fullWidth
      />
      <Input
        type="number"
        min={0}
        label={t('schedulingAdmin.services.buffer')}
        placeholder={t('schedulingAdmin.services.sameAsService')}
        value={values.buffer}
        onChange={e => setValues({ ...values, buffer: e.target.value })}
        fullWidth
      />
      <Input
        type="number"
        min={0}
        step="0.01"
        label={t('schedulingAdmin.services.price')}
        placeholder={t('schedulingAdmin.services.sameAsService')}
        value={values.price}
        onChange={e => setValues({ ...values, price: e.target.value })}
        fullWidth
      />
      <div className="flex gap-1 col-span-2 sm:col-span-1">
        {dirty && (
          <Button
            size="sm"
            loading={saving}
            disabled={!values.name.trim() || !values.duration}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(values);
              } finally {
                setSaving(false);
              }
            }}
          >
            {t('common.save')}
          </Button>
        )}
        <Button size="sm" variant="ghost" title={t('common.remove')} onClick={onRemove}>
          <span className="material-icons text-base">delete</span>
        </Button>
      </div>
    </div>
  );
};

export const ServicesTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t, i18n } = useTranslation();
  const { data: services = [], isLoading } = useServices(profile.id);
  const { data: resources = [] } = useResources(profile.id);
  const mutations = useServiceMutations(profile.id);

  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [variantError, setVariantError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Service | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const inactiveCount = services.filter(service => !service.is_active).length;
  const visibleServices = showInactive ? services : services.filter(service => service.is_active);

  // Keep the modal's variants in sync with the refetched list.
  const current = form?.id ? services.find(service => service.id === form.id) : undefined;

  const openForm = (service?: Service) => {
    setFieldErrors({});
    setFormError(null);
    setVariantError(null);
    setForm(service ? toForm(service) : EMPTY_FORM);
  };

  const errorMessage = (error: unknown) =>
    error instanceof SchedulingApiError ? error.message : t('schedulingAdmin.errors.generic');

  const handleSave = async () => {
    if (!form) return;
    const data = {
      name: form.name.trim(),
      description: form.description.trim(),
      duration_minutes: Number(form.duration),
      buffer_after_minutes: Number(form.buffer) || 0,
      price: form.price === '' ? null : form.price,
      resources: form.resources,
      is_active: form.isActive,
    };
    setFieldErrors({});
    setFormError(null);
    try {
      if (form.id) {
        await mutations.update.mutateAsync({ id: form.id, data });
        setForm(null);
      } else {
        // Stay open after creating so variants can be added right away.
        const created = await mutations.create.mutateAsync(data);
        setForm({ ...form, id: created.id });
      }
    } catch (error) {
      if (error instanceof SchedulingApiError) {
        setFieldErrors(error.fieldErrors);
        setFormError(error.nonFieldErrors[0] ?? null);
      } else {
        setFormError(t('schedulingAdmin.errors.generic'));
      }
    }
  };

  const variantPayload = (values: {
    name: string;
    duration: string;
    buffer: string;
    price: string;
  }) => ({
    name: values.name.trim(),
    duration_minutes: Number(values.duration),
    buffer_after_minutes: values.buffer === '' ? null : Number(values.buffer),
    price: values.price === '' ? null : values.price,
  });

  const runVariant = async (action: () => Promise<unknown>) => {
    setVariantError(null);
    try {
      await action();
    } catch (error) {
      setVariantError(errorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await mutations.remove.mutateAsync(toDelete.id);
      setToDelete(null);
    } catch (error) {
      if (isProtectedByBookings(error)) {
        setDeleteBlocked(true);
      } else {
        setDeleteError(errorMessage(error));
      }
    }
  };

  const handleDeactivate = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await mutations.update.mutateAsync({ id: toDelete.id, data: { is_active: false } });
      setToDelete(null);
    } catch {
      setDeleteError(t('schedulingAdmin.errors.generic'));
    }
  };

  const toggleResource = (id: string, checked: boolean) => {
    if (!form) return;
    setForm({
      ...form,
      resources: checked ? [...form.resources, id] : form.resources.filter(item => item !== id),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
          {t('schedulingAdmin.services.intro')}
        </p>
        <Button onClick={() => openForm()}>{t('schedulingAdmin.services.add')}</Button>
      </div>

      <ShowInactiveToggle count={inactiveCount} checked={showInactive} onChange={setShowInactive} />

      {isLoading ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : services.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('schedulingAdmin.services.empty')}
        </p>
      ) : (
        visibleServices.length > 0 && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
            {visibleServices.map(service => (
              <div
                key={service.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white truncate">
                      {service.name}
                    </span>
                    {!service.is_active && (
                      <Badge variant="warning" size="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDuration(service.duration_minutes)}
                    {service.buffer_after_minutes > 0 &&
                      ` + ${formatDuration(service.buffer_after_minutes)} ${t('schedulingAdmin.services.bufferShort')}`}
                    {service.price !== null && ` · ${formatPrice(service.price, i18n.language)}`}
                  </p>
                  {service.variants.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {service.variants.map(variant => (
                        <Badge key={variant.id} variant="outline" size="sm">
                          {variant.name} · {formatDuration(variant.duration_minutes)}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    {service.resources.length === 0
                      ? t('schedulingAdmin.services.everyone')
                      : t('schedulingAdmin.services.restricted', {
                          names: resources
                            .filter(resource => service.resources.includes(resource.id))
                            .map(resource => resource.name)
                            .join(', '),
                        })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openForm(service)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title={t('common.delete')}
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteBlocked(false);
                      setToDelete(service);
                    }}
                  >
                    <span className="material-icons text-lg">delete</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Modal
        isOpen={!!form}
        onClose={() => setForm(null)}
        title={
          form?.id
            ? t('schedulingAdmin.services.editTitle')
            : t('schedulingAdmin.services.addTitle')
        }
        size="xl"
      >
        {form && (
          <div className="space-y-5">
            <Input
              label={t('schedulingAdmin.fields.name')}
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder={t('schedulingAdmin.services.namePlaceholder')}
              errorText={fieldErrors.name}
              state={fieldErrors.name ? 'error' : 'default'}
              fullWidth
              required
            />
            <Textarea
              label={t('schedulingAdmin.services.description')}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              helperText={t('schedulingAdmin.services.descriptionHelp')}
              fullWidth
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                type="number"
                min={1}
                label={t('schedulingAdmin.services.duration')}
                value={form.duration}
                onChange={e => setForm({ ...form, duration: e.target.value })}
                errorText={fieldErrors.duration_minutes}
                fullWidth
                required
              />
              <Input
                type="number"
                min={0}
                label={t('schedulingAdmin.services.buffer')}
                value={form.buffer}
                onChange={e => setForm({ ...form, buffer: e.target.value })}
                helperText={t('schedulingAdmin.services.bufferHelp')}
                errorText={fieldErrors.buffer_after_minutes}
                fullWidth
              />
              <Input
                type="number"
                min={0}
                step="0.01"
                label={t('schedulingAdmin.services.price')}
                value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
                errorText={fieldErrors.price}
                fullWidth
              />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('schedulingAdmin.services.whoCanDoIt')}
              </p>
              <p className="text-xs text-gray-500">
                {t('schedulingAdmin.services.whoCanDoItHelp')}
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {resources.map(resource => (
                  <Checkbox
                    key={resource.id}
                    label={resource.name}
                    checked={form.resources.includes(resource.id)}
                    onChange={e => toggleResource(resource.id, e.target.checked)}
                  />
                ))}
              </div>
              {fieldErrors.resources && (
                <p className="text-sm text-red-600">{fieldErrors.resources}</p>
              )}
            </div>

            <Checkbox
              label={t('schedulingAdmin.services.active')}
              checked={form.isActive}
              onChange={e => setForm({ ...form, isActive: e.target.checked })}
            />

            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setForm(null)}>
                {form.id ? t('common.close') : t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                loading={mutations.create.isPending || mutations.update.isPending}
                disabled={!form.name.trim() || !form.duration}
              >
                {t('common.save')}
              </Button>
            </div>

            {form.id ? (
              <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-5">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    {t('schedulingAdmin.services.variants')}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('schedulingAdmin.services.variantsHelp')}
                  </p>
                </div>
                {(current?.variants ?? []).map(variant => (
                  <VariantRow
                    key={variant.id}
                    variant={variant}
                    onSave={values =>
                      runVariant(() =>
                        mutations.updateVariant.mutateAsync({
                          id: variant.id,
                          data: variantPayload(values),
                        })
                      )
                    }
                    onRemove={() =>
                      runVariant(() => mutations.removeVariant.mutateAsync(variant.id))
                    }
                  />
                ))}
                <NewVariant
                  onAdd={values =>
                    runVariant(() =>
                      mutations.createVariant.mutateAsync({
                        service: form.id as string,
                        ...variantPayload(values),
                      })
                    )
                  }
                />
                {variantError && <p className="text-sm text-red-600">{variantError}</p>}
              </div>
            ) : (
              <p className="text-xs text-gray-500">{t('schedulingAdmin.services.saveFirst')}</p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        title={t('schedulingAdmin.services.deleteTitle')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {deleteBlocked
              ? t('schedulingAdmin.errors.protectedByBookings', { name: toDelete?.name })
              : t('schedulingAdmin.services.deleteText', { name: toDelete?.name })}
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setToDelete(null)}>
              {t('common.cancel')}
            </Button>
            {!deleteBlocked ? (
              <Button variant="danger" onClick={handleDelete} loading={mutations.remove.isPending}>
                {t('common.delete')}
              </Button>
            ) : (
              toDelete?.is_active && (
                <Button onClick={handleDeactivate} loading={mutations.update.isPending}>
                  {t('schedulingAdmin.errors.deactivateInstead')}
                </Button>
              )
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

const NewVariant: React.FC<{
  onAdd: (values: {
    name: string;
    duration: string;
    buffer: string;
    price: string;
  }) => Promise<void>;
}> = ({ onAdd }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({ name: '', duration: '', buffer: '', price: '' });
  const [saving, setSaving] = useState(false);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 items-end rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
      <Input
        label={t('schedulingAdmin.services.variantName')}
        placeholder={t('schedulingAdmin.services.variantPlaceholder')}
        value={values.name}
        onChange={e => setValues({ ...values, name: e.target.value })}
        fullWidth
      />
      <Input
        type="number"
        min={1}
        label={t('schedulingAdmin.services.duration')}
        value={values.duration}
        onChange={e => setValues({ ...values, duration: e.target.value })}
        fullWidth
      />
      <Input
        type="number"
        min={0}
        label={t('schedulingAdmin.services.buffer')}
        placeholder={t('schedulingAdmin.services.sameAsService')}
        value={values.buffer}
        onChange={e => setValues({ ...values, buffer: e.target.value })}
        fullWidth
      />
      <Input
        type="number"
        min={0}
        step="0.01"
        label={t('schedulingAdmin.services.price')}
        placeholder={t('schedulingAdmin.services.sameAsService')}
        value={values.price}
        onChange={e => setValues({ ...values, price: e.target.value })}
        fullWidth
      />
      <Button
        size="sm"
        className="col-span-2 sm:col-span-1"
        loading={saving}
        disabled={!values.name.trim() || !values.duration}
        onClick={async () => {
          setSaving(true);
          try {
            await onAdd(values);
            setValues({ name: '', duration: '', buffer: '', price: '' });
          } finally {
            setSaving(false);
          }
        }}
      >
        {t('schedulingAdmin.services.addVariant')}
      </Button>
    </div>
  );
};
